import type { SortingState, Updater } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useShallow } from "zustand/react/shallow"

import { type PriceChangeSubmission } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"
import { usePriceChangeHistorySearchStore } from "@/screens/Pricing/stores"
import type { PriceChangeHistoryRoute } from "@/screens/Pricing/types"
import type { PriceChangeHistorySearchParams } from "@/screens/Pricing/types/searchParams"

interface UseSellingPriceHistoryListReturn {
  priceChangeHistoryItems: PriceChangeSubmission[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  searchParams: PriceChangeHistorySearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof PriceChangeHistorySearchParams>(
    key: K,
    value: PriceChangeHistorySearchParams[K]
  ) => void
  handleFilterRemove: (key: keyof PriceChangeHistorySearchParams, value?: string | number) => void
  handleFilterClear: () => void
  handleScroll: () => void
}

interface UsePriceChangeHistoryListParams {
  route: PriceChangeHistoryRoute
  filterKeys: (keyof PriceChangeHistorySearchParams)[]
}

export const usePriceChangeHistoryList = ({
  route = "/app/pricing/_layout/history/price-change",
  filterKeys,
}: UsePriceChangeHistoryListParams): UseSellingPriceHistoryListReturn => {
  const store = usePriceChangeHistorySearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const {
    data: priceChangeHistoryItems,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  } = useListPageBase<PriceChangeSubmission, PriceChangeHistorySearchParams>({
    route,
    store,
    filterKeys,
  })

  return {
    priceChangeHistoryItems,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
